import { EmptyData } from '@/components/lib/EmptyData'
import { Loading } from '@/components/lib/Loading'
import LockerMenu from '@/components/Menu/LockerMenu'
import useMediaQuery from '@/hooks/use-media-query'
import { useUserRole } from '@/hooks/useUserRole'
import AuthService from '@/services/Auth/AuthService'
import LockerService from '@/services/Locker/LockerService'
import { LockerType } from '@/types/LockerType'
import { flashStore } from '@/utils/Store'
import * as Sentry from '@sentry/react'
import { AxiosError, AxiosResponse } from 'axios'
import { Plus } from 'lucide-react'
import React from 'react'
import { Button, Card, Col, Container, Table } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import Swal, { SweetAlertResult } from 'sweetalert2'

interface ErrorResponse {
  response: AxiosResponse
  hydra: {
    description: string
  }
}
const MD_BREAKPOINT = '(min-width: 769px)'

export const LockerListTable = () => {
  const isDesktop = useMediaQuery(MD_BREAKPOINT)
  /* States / Hooks
   *******************************************************************************************/
  const [lockers, setLockers] = React.useState<LockerType[]>([])
  const [loading, setLoading] = React.useState<boolean>(true)
  const [nextLoading, setNextLoading] = React.useState<boolean>(false)
  const [nextPage, setNextPage] = React.useState<string | undefined>()
  const [currentPage] = React.useState<number>(1)
  const [perPage] = React.useState<number>(10)
  const flash = flashStore()
  const myRole = useUserRole()

  /* useEffect
   *******************************************************************************************/
  React.useEffect(() => {
    getData(currentPage, perPage)
  }, [currentPage, perPage])

  /* Functions
   *******************************************************************************************/
  const getData = (page: number, perPage: number) => {
    LockerService.getLockerPerPage(page, perPage)
      .then((response: AxiosResponse) => {
        setLockers(response.data['hydra:member'])
        setNextPage(response.data['hydra:view']['hydra:next'])
      })
      .catch((error: AxiosError) => {
        AuthService.refreshToken()
        Sentry.captureException(error)
      })
      .finally(() => setLoading(false))
  }

  const handleNextPage = () => {
    setNextLoading(true)
    LockerService.getLockerLoadNextPage(nextPage)
      .then((response: AxiosResponse) => {
        setLockers(lockers.concat(response.data['hydra:member']))
        setNextPage(response.data['hydra:view']['hydra:next'])
      })
      .catch((error: AxiosError) => {
        AuthService.refreshToken()
        Sentry.captureException(error)
      })
      .finally(() => setNextLoading(false))
  }

  const handleDelete = (data: LockerType) => {
    Swal.fire({
      title: 'Confirmation !',
      text: 'Voulez-vous vraiment supprimer la machine #' + data.id + ' ?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Oui, je confirme',
      cancelButtonText: 'Non',
    }).then((result: SweetAlertResult) => {
      if (result.isConfirmed) {
        LockerService._delete(Number(data.id))
          .then(() => {
            flash.setMessage('success', 'Machine supprimée !')
            getData(currentPage, perPage)
          })
          .catch((e: ErrorResponse) => {
            if (e?.response?.status == 403) {
              flash.setMessage('error', e?.response?.data['hydra:description'])
            }
          })
      }
    })
  }

  /**
   * Mobile Component
   */
  const lockerListMobile = () => (
    <Container fluid className='px-0'>
      {lockers.map((locker) => {
        const lockerMenuProps = { locker, handleDelete }
        return (
          <Card key={locker.id} className='border-4 border-start border-warning  shadow px-0 py-5 mb-4 w-100'>
            <div className='d-flex align-items-center gap-0 px-2'>
              <Col xs={11} className='d-flex flex-column justify-content-center'>
                <Link to={'/lockers/detail/' + locker?.id}>
                  <div className='d-flex flex-column justify-content-center align-items-center'>
                    <span className='text-lg'>{locker.location}</span>
                    <span className='font-75'>
                      ({locker.cleveronApmId} / type : {locker.type})
                    </span>
                  </div>
                </Link>
              </Col>
              <Col xs={1}>
                <LockerMenu lockerMenuProps={lockerMenuProps} />
              </Col>
            </div>
          </Card>
        )
      })}
    </Container>
  )

  /**
   * Mobile Desktop
   */
  const lockerListTableDesktop = () => (
    <Table striped hover responsive>
      <thead>
        <tr>
          <th>#</th>
          <th>ID</th>
          <th>Modèle</th>
          <th>Nom utilisé</th>
          <th>Emplacement</th>
          <th>Commune</th>
          {myRole === import.meta.env.VITE_APP_ROLE_SUPER_ADMIN && <th>Actions</th>}
        </tr>
      </thead>
      <tbody>
        {lockers.map((locker: LockerType) => {
          const lockerMenuProps = { locker, handleDelete }
          return (
            <tr key={locker?.id}>
              <td>
                <Link to={'/lockers/detail/' + locker?.id}>{locker?.id}</Link>
              </td>
              <td>
                <Link to={'/lockers/detail/' + locker?.id}>{locker?.cleveronApmId}</Link>
              </td>
              <td>
                <Link to={'/lockers/detail/' + locker?.id}>{locker?.type}</Link>
              </td>
              <td>
                <Link to={'/lockers/detail/' + locker?.id}>{locker?.shortLocation}</Link>
              </td>
              <td>
                <Link to={'/lockers/detail/' + locker?.id}>{locker?.location}</Link>
              </td>
              <td>
                <Link to={'/lockers/detail/' + locker?.id}>{locker?.city}</Link>
              </td>
              {myRole === import.meta.env.VITE_APP_ROLE_SUPER_ADMIN && (
                <td>
                  <LockerMenu lockerMenuProps={lockerMenuProps} />
                </td>
              )}
            </tr>
          )
        })}
      </tbody>
    </Table>
  )

  /* Render
   *******************************************************************************************/
  return (
    <>
      {loading ? (
        <Loading />
      ) : lockers?.length === 0 ? (
        <EmptyData />
      ) : (
        <>
          {isDesktop ? lockerListTableDesktop() : lockerListMobile()}
          {nextPage !== undefined && (
            <div className='ms-auto mb-3 me-3'>
              <Button variant='dark' className='d-inline w-20' onClick={handleNextPage}>
                {nextLoading ? (
                  'En cours de chargement...'
                ) : (
                  <>
                    <Plus size={20} /> Charger plus{' '}
                  </>
                )}
              </Button>
            </div>
          )}
        </>
      )}
    </>
  )
}
